import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { Button } from '../components/ui/button';
import { ScoreBar, ScoreNumber, SeverityChip, StatusChip } from '../components/evidence';
import { BANDS, formatScore, inspectionStage, scoreBand } from '../lib/damage';

import { API } from '../lib/api';

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  return `$${toNumber(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function shortDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function typeLabel(type) {
  const t = String(type || '').replace(/_/g, '-');
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Inspection';
}

// Scored by the model but not yet approved: the landlord's next job.
const needsReview = (insp) => inspectionStage(insp) === 'review';

// Round the chart's top value up to a readable number so gridlines land on clean amounts.
function niceMax(value) {
  if (value <= 0) return 100;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((s) => s * magnitude >= value) || 10;
  return step * magnitude;
}

const delay = (s) => ({ '--rs-delay': `${s}s` });

// Written out in full so Tailwind generates them.
const BAND_TEXT = { low: 'text-sev-low', med: 'text-sev-med', high: 'text-sev-high' };
const BAND_SEGMENT = {
  low: 'border-sev-low bg-sev-low-soft',
  med: 'border-sev-med bg-sev-med-soft',
  high: 'border-sev-high bg-sev-high-soft',
};

function SectionTitle({ children, id }) {
  return <h2 id={id} className="m-0 font-display text-[22px] font-bold leading-7">{children}</h2>;
}

function Stat({ label, children, index }) {
  return (
    <div className="rs-rise flex flex-col gap-1.5 bg-surface px-[18px] py-4" style={delay(0.1 + index * 0.05)}>
      <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">{label}</p>
      {children}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/stats`);
        setStats(data);
      } catch {
        toast.error("Couldn't load your dashboard. Refresh to try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const properties = useMemo(() => stats?.properties_preview || [], [stats]);
  const recentInspections = useMemo(() => stats?.recent_inspections || [], [stats]);
  const toReview = useMemo(() => recentInspections.find(needsReview), [recentInspections]);

  const conditionMix = useMemo(() => {
    const counts = { low: 0, med: 0, high: 0 };
    recentInspections.forEach((i) => {
      const band = scoreBand(i.overall_score);
      if (band) counts[band.key] += 1;
    });
    return { ...counts, total: counts.low + counts.med + counts.high };
  }, [recentInspections]);

  const repairTrend = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleDateString('en-US', { month: 'short' }),
        year: d.getFullYear(),
        value: 0,
      });
    }
    const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
    recentInspections.forEach((insp) => {
      const d = new Date(insp.completed_at || insp.created_at);
      if (Number.isNaN(d.getTime())) return;
      const m = byKey[`${d.getFullYear()}-${d.getMonth()}`];
      if (m) m.value += toNumber(insp.total_repair_cost);
    });
    const max = niceMax(Math.max(...months.map((m) => m.value), 0));
    return { series: months, max };
  }, [recentInspections]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading dashboard">
          <div className="h-9 w-48 rounded-md bg-sunken" />
          <div className="h-[92px] rounded-md bg-sunken" />
          <div className="grid gap-6 lg:grid-cols-[7fr_5fr]">
            <div className="h-[420px] rounded-md bg-sunken" />
            <div className="h-[420px] rounded-md bg-sunken" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const avgScore = stats?.avg_damage_score;
  const avgBand = scoreBand(avgScore);
  const inProgress = toNumber(stats?.active_inspections);
  const totalProps = toNumber(stats?.total_properties);
  const latest = repairTrend.series[repairTrend.series.length - 1];

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="flex flex-col gap-6">
        <OnboardingChecklist />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="m-0 font-display text-3xl font-bold leading-9 tracking-[-0.01em]" data-testid="dashboard-heading">Dashboard</h1>
            <p className="m-0 mt-1 text-ink-muted">
              {totalProps} {totalProps === 1 ? 'property' : 'properties'} · {inProgress} {inProgress === 1 ? 'inspection' : 'inspections'} in progress
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="view-properties-btn">
              <Link to="/properties">View properties</Link>
            </Button>
            <Button asChild data-testid="new-inspection-btn">
              <Link to="/inspections"><Plus strokeWidth={1.5} />New inspection</Link>
            </Button>
          </div>
        </div>

        {toReview && (
          <div className="rs-pop flex flex-wrap items-center gap-x-5 gap-y-3 rounded-md border border-line bg-surface px-[18px] py-3.5" data-testid="review-banner">
            <StatusChip tone="review" className="rs-pulse">Needs your review</StatusChip>
            <p className="m-0 min-w-0 flex-[1_1_320px]">
              <b>{toReview.property_name}</b>
              <span className="text-ink-muted"> · {typeLabel(toReview.inspection_type)} · scored </span>
              <ScoreNumber score={toReview.overall_score} className="text-base" />
              <span className="font-mono font-semibold text-ink-muted">/100</span>
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to={`/inspections/${toReview.id}`}>Review findings</Link>
            </Button>
          </div>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))] gap-px overflow-hidden rounded-md border border-line bg-line" data-testid="stats-grid">
          <Stat label="Total repair costs" index={0}>
            <p className="m-0 font-mono text-[26px] font-bold leading-8 tabular">{money(stats?.total_repair_cost)}</p>
          </Stat>
          <Stat label="Tenant chargeables" index={1}>
            <p className="m-0 font-mono text-[26px] font-bold leading-8 tabular">{money(stats?.total_chargeable)}</p>
          </Stat>
          <Stat label="Active inspections" index={2}>
            <p className="m-0 font-mono text-[26px] font-bold leading-8 tabular">{inProgress}</p>
          </Stat>
          <Stat label="Properties" index={3}>
            <p className="m-0 font-mono text-[26px] font-bold leading-8 tabular">{totalProps}</p>
          </Stat>
          <Stat label="Avg damage score" index={4}>
            <p className="m-0 font-mono text-[26px] font-bold leading-8 tabular">
              <span className={avgBand ? BAND_TEXT[avgBand.key] : 'text-ink-muted'}>{formatScore(avgScore)}</span>
              <span className="text-sm font-semibold text-ink-muted">/100</span>
            </p>
            <ScoreBar score={avgScore} className="mt-1 w-full max-w-[160px]" />
          </Stat>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[7fr_5fr]">
          <section aria-labelledby="portfolio-title" className="rs-rise flex min-w-0 flex-col gap-7 rounded-md border border-line bg-surface p-6" style={delay(0.25)}>
            <SectionTitle id="portfolio-title">Portfolio</SectionTitle>

            <div className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="m-0 font-body text-[15px] font-bold">Condition of recent inspections</h3>
                <span className="font-mono text-sm font-semibold text-ink-muted">{conditionMix.total} scored</span>
              </div>
              {conditionMix.total > 0 ? (
                <>
                  <div
                    aria-hidden="true"
                    className="grid h-7 gap-[3px]"
                    style={{ gridTemplateColumns: ['low', 'med', 'high'].filter((k) => conditionMix[k]).map((k) => `${conditionMix[k]}fr`).join(' ') }}
                  >
                    {['low', 'med', 'high'].filter((k) => conditionMix[k]).map((k, i) => (
                      <span key={k} className={`rs-growx border ${BAND_SEGMENT[k]}`} style={delay(0.5 + i * 0.1)} />
                    ))}
                  </div>
                  <ul className="m-0 flex list-none flex-wrap gap-x-6 gap-y-2 p-0 text-sm leading-5">
                    {[['low', '0 to 40'], ['med', '41 to 60'], ['high', '61 to 100']].map(([k, range]) => (
                      <li key={k} className="inline-flex items-center gap-1.5">
                        <SeverityChip severity={k} />
                        <span className="text-ink-muted">{range}</span>
                        <b className="font-mono">{conditionMix[k]}</b>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="m-0 text-ink-muted">Scores appear here after your first analyzed photo.</p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="m-0 font-body text-[15px] font-bold">Repair costs by month</h3>
                <span className="font-mono text-sm font-semibold text-ink-muted">
                  {repairTrend.series[0].label} to {latest.label} {latest.year}
                </span>
              </div>
              <div
                role="img"
                aria-label={`Repair costs: ${repairTrend.series.map((m) => `${m.label} ${money(m.value)}`).join(', ')}`}
                className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-2 gap-y-1.5"
              >
                <div aria-hidden="true" className="relative h-[140px] text-right font-mono text-xs font-semibold leading-3 text-ink-muted">
                  <span className="absolute right-0 -top-1.5">{money(repairTrend.max)}</span>
                  <span className="absolute right-0 top-[64px]">{money(repairTrend.max / 2)}</span>
                  <span className="absolute right-0 -bottom-1.5">$0</span>
                </div>
                <div aria-hidden="true" className="relative h-[140px] border-b border-line-strong">
                  <span className="absolute inset-x-0 top-0 border-t border-dashed border-line" />
                  <span className="absolute inset-x-0 top-[70px] border-t border-dashed border-line" />
                  <div className="absolute inset-0 grid grid-cols-6 items-end gap-4 px-2">
                    {repairTrend.series.map((m, i) => {
                      const isLatest = i === repairTrend.series.length - 1;
                      const h = Math.round((m.value / repairTrend.max) * 140);
                      return (
                        <span
                          key={m.key}
                          className={`rs-growy relative ${isLatest ? 'bg-marker' : 'bg-ink-muted'}`}
                          style={{ height: `${h}px`, ...delay(0.55 + i * 0.07) }}
                        >
                          {isLatest && m.value > 0 && (
                            <b className="rs-fade absolute -top-[22px] left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[13px] leading-4" style={delay(1.15)}>
                              {money(m.value)}
                            </b>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <span />
                <div aria-hidden="true" className="grid grid-cols-6 gap-4 px-2 text-center font-mono text-[13px] font-semibold text-ink-muted">
                  {repairTrend.series.map((m, i) => (
                    <span key={m.key} className={i === repairTrend.series.length - 1 ? 'font-bold text-ink' : ''}>{m.label}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <h3 className="m-0 mb-2 font-body text-[15px] font-bold">Properties</h3>
              {properties.length === 0 ? (
                <p className="m-0 text-ink-muted">
                  No properties yet. <Link to="/properties" className="font-bold text-ink underline decoration-marker decoration-2 underline-offset-[3px]">Add your first property</Link>
                </p>
              ) : (
                properties.slice(0, 4).map((p, i) => (
                  <Link
                    key={p.id}
                    to={`/properties/${p.id}`}
                    className="rs-rise grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-t border-line py-2.5 text-ink no-underline hover:bg-sunken"
                    style={delay(0.8 + i * 0.06)}
                  >
                    <span className="min-w-0">
                      <b className="block truncate">{p.name}</b>
                      <span className="block truncate text-sm text-ink-muted">{p.address}</span>
                    </span>
                    <ScoreNumber score={p.overall_health} />
                  </Link>
                ))
              )}
            </div>
          </section>

          <section aria-labelledby="recent-title" className="rs-rise min-w-0 overflow-hidden rounded-md border border-line bg-surface" style={delay(0.32)} data-testid="recent-inspections">
            <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-5">
              <SectionTitle id="recent-title">Recent inspections</SectionTitle>
              <Link to="/inspections" className="text-sm font-bold text-ink underline decoration-marker decoration-2 underline-offset-[3px]">See all</Link>
            </div>
            {recentInspections.length === 0 ? (
              <div className="flex flex-col items-start gap-3 border-t border-line p-5">
                <ClipboardCheck className="h-6 w-6 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
                <p className="m-0 font-bold">Your first inspection</p>
                <p className="m-0 max-w-[46ch] text-ink-muted">Start one at move-in so you have something to compare at move-out.</p>
                <Button asChild data-testid="start-first-inspection"><Link to="/inspections"><Plus strokeWidth={1.5} />New inspection</Link></Button>
              </div>
            ) : (
              recentInspections.map((insp, i) => (
                <Link
                  key={insp.id}
                  to={`/inspections/${insp.id}`}
                  className="rs-rise grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 border-t border-line px-5 py-3.5 text-ink no-underline hover:bg-sunken"
                  style={delay(0.45 + i * 0.06)}
                  data-testid={`inspection-row-${insp.id}`}
                >
                  <span className="min-w-0">
                    <b className="block truncate">{insp.property_name}</b>
                    <span className="font-mono text-sm font-semibold text-ink-muted">
                      {typeLabel(insp.inspection_type)} · {shortDate(insp.completed_at || insp.created_at)}
                    </span>
                  </span>
                  <ScoreNumber score={insp.overall_score} className="text-right text-[22px] leading-7" />
                  <span className="flex flex-wrap gap-1.5">
                    {scoreBand(insp.overall_score) && <SeverityChip score={insp.overall_score} />}
                    {needsReview(insp) ? (
                      <StatusChip tone="review">Needs review</StatusChip>
                    ) : insp.status === 'completed' ? (
                      <StatusChip done>Approved</StatusChip>
                    ) : (
                      <StatusChip>In progress</StatusChip>
                    )}
                  </span>
                </Link>
              ))
            )}
          </section>
        </div>

        <p className="sr-only">
          Damage bands: {BANDS.low.label} 0 to 40, {BANDS.med.label} 41 to 60, {BANDS.high.label} 61 to 100.
        </p>
      </div>
    </DashboardLayout>
  );
}
